import { Document } from "flexsearch";
import GhostContentAPI from "@tryghost/content-api";
import type { Author, GhostAPI, PostOrPage, Tag } from "@tryghost/content-api";
import type {
  GhostPost,
  GhostAuthor,
  GhostTag,
  SearchResults,
} from "../../types";

type EnrichedResult<T> = {
  field: string;
  result: Array<{ id: string | number; doc: T }>;
};

export default class SearchIndex {
  private api: GhostAPI;
  private postsIndex: Document<PostOrPage, true>;
  private authorsIndex: Document<Author, true>;
  private tagsIndex: Document<Tag, true>;

  constructor({ adminUrl, apiKey }: { adminUrl: string; apiKey: string }) {
    this.api = new GhostContentAPI({
      url: adminUrl,
      key: apiKey,
      version: "v5.0",
    });

    this.postsIndex = new Document({
      tokenize: "forward",
      document: { id: "id", index: ["title", "excerpt"], store: true },
    });
    this.authorsIndex = new Document({
      tokenize: "forward",
      document: { id: "id", index: ["name"], store: true },
    });
    this.tagsIndex = new Document({
      tokenize: "forward",
      document: { id: "id", index: ["name"], store: true },
    });
  }

  private updatePostIndex(posts: PostOrPage[]): void {
    posts.forEach((post) => this.postsIndex.add(post));
  }

  private updateAuthorsIndex(authors: Author[]): void {
    authors.forEach((author) => this.authorsIndex.add(author));
  }

  private updateTagsIndex(tags: Tag[]): void {
    tags.forEach((tag) => this.tagsIndex.add(tag));
  }

  private async fetchAllPosts() {
    let page = 1;
    const allPosts: PostOrPage[] = [];
    let keepFetching = true;

    while (keepFetching) {
      const response = await this.api.posts.browse({
        limit: "10000",
        fields: "id,slug,title,excerpt,url,updated_at,visibility",
        order: "updated_at DESC",
        page,
      });

      allPosts.push(...response);

      if (response.meta.pagination.next) {
        page = response.meta.pagination.next;
      } else {
        keepFetching = false;
      }
    }
    return allPosts;
  }

  private async fetchAllAuthors() {
    let page = 1;
    const allAuthors: Author[] = [];
    let keepFetching = true;

    while (keepFetching) {
      const response = await this.api.authors.browse({
        limit: "10000",
        fields: "id,slug,name,url,profile_image",
        order: "updated_at DESC",
        page,
      });

      allAuthors.push(...response);

      if (response.meta.pagination.next) {
        page = response.meta.pagination.next;
      } else {
        keepFetching = false;
      }
    }
    return allAuthors;
  }

  private async fetchAllTags() {
    let page = 1;
    const allTags: Tag[] = [];
    let keepFetching = true;

    while (keepFetching) {
      const response = await this.api.tags.browse({
        limit: "10000",
        fields: "id,slug,name,url",
        order: "updated_at DESC",
        filter: "visibility:public",
        page,
      });

      allTags.push(...response);

      if (response.meta.pagination.next) {
        page = response.meta.pagination.next;
      } else {
        keepFetching = false;
      }
    }
    return allTags;
  }

  async init(): Promise<void> {
    const posts = await this.fetchAllPosts();
    if (posts) {
      this.updatePostIndex(posts);
    }

    const authors = await this.fetchAllAuthors();
    if (authors) {
      this.updateAuthorsIndex(authors);
    }

    const tags = await this.fetchAllTags();
    if (tags) {
      this.updateTagsIndex(tags);
    }
  }

  private normalizeSearchResult<T>(result: EnrichedResult<T>[]): T[] {
    const normalized: T[] = [];
    const usedIds: Record<string, boolean> = {};
    result.forEach((resultItem) => {
      resultItem.result.forEach((doc) => {
        const id = String(doc.id);
        if (!usedIds[id]) {
          normalized.push(doc.doc);
          usedIds[id] = true;
        }
      });
    });
    return normalized;
  }

  search(value: string): SearchResults {
    const posts = this.postsIndex.search(value, {
      enrich: true,
    }) as unknown as EnrichedResult<GhostPost>[];
    const authors = this.authorsIndex.search(value, {
      enrich: true,
    }) as unknown as EnrichedResult<GhostAuthor>[];
    const tags = this.tagsIndex.search(value, {
      enrich: true,
    }) as unknown as EnrichedResult<GhostTag>[];

    return {
      posts: this.normalizeSearchResult(posts),
      authors: this.normalizeSearchResult(authors),
      tags: this.normalizeSearchResult(tags),
    };
  }
}
